import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { Button, Card, Badge, Spinner } from 'react-bootstrap';
import { getFlightStatus } from '../services/flightTracker';
import { Member, FlightStatus } from '../../../shared/types';

export default function MemberDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [flightStatus, setFlightStatus] = useState<FlightStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isOwnRecord = () => {
    return auth.currentUser?.email === member?.Email;
  };

  useEffect(() => {
    const fetchMemberAndFlight = async () => {
      try {
        if (!id) return;
        
        const memberDocRef = doc(db, 'members', id);
        const docSnap = await getDoc(memberDocRef);
        
        if (docSnap.exists()) {
          const memberData = { id: docSnap.id, ...docSnap.data() } as Member;
          setMember(memberData);
          
          // Fetch flight status if flight number exists
          if (memberData['Flight # incoming']) {
            const status = await getFlightStatus(memberData['Flight # incoming']);
            if (status) {
              setFlightStatus(status as FlightStatus);
            }
          }
        } else {
          setError('Member not found');
        }
      } catch (err) {
        console.error('Error fetching member details:', err);
        setError('Error fetching member details');
      } finally {
        setLoading(false);
      }
    };
    fetchMemberAndFlight();
  }, [id]);

  const getStatusBadge = (status: string) => {
    if (!status) return null;
    
    let variant = 'secondary';
    switch (status.toLowerCase()) {
      case 'scheduled':
        variant = 'info';
        break;
      case 'active':
      case 'en-route':
        variant = 'primary';
        break;
      case 'landed':
        variant = 'success';
        break;
      case 'delayed':
        variant = 'warning';
        break;
      case 'cancelled':
        variant = 'danger';
        break;
      default:
        variant = 'secondary';
    }
    
    return (
      <Badge bg={variant} className="ms-2">
        {status.toUpperCase()}
      </Badge>
    );
  };

  const formatInstagramLink = (handle: string | undefined | null): string | undefined => {
    if (!handle) return undefined;
    // Remove @ if present and clean the handle
    const cleanHandle = handle.replace('@', '').trim();
    return `https://instagram.com/${cleanHandle}`;
  };

  if (loading) return <div>Loading member details...</div>;
  if (error) return <div>{error}</div>;
  if (!member) return <div>No member found</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Button 
          variant="outline-primary" 
          onClick={() => navigate('/members')}
        >
          ← Back to Members
        </Button>
        {isOwnRecord() && (
          <Button 
            variant="primary"
            onClick={() => navigate(`/members/${id}/edit`)}
          >
            Edit My Information
          </Button>
        )}
      </div>

      <h2>Shredder Details</h2>
      
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex flex-column flex-md-row align-items-md-start">
            <div 
              className="text-center mb-4 mb-md-0"
              style={{ 
                width: '200px',
                minWidth: '200px',
                marginRight: '0',
                marginBottom: '20px',
                position: 'relative',
                alignSelf: 'center'
              }}
            >
              <div
                style={{
                  width: '200px',
                  height: '200px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '10px'
                }}
              >
                {member.photoURL ? (
                  <img 
                    src={member.photoURL} 
                    alt={member.Name} 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{ color: '#adb5bd', textAlign: 'center' }}>
                    <i className="bi bi-person" style={{ fontSize: '3rem' }}></i>
                    <div>No Photo</div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-grow-1 ms-md-4">
              <Card.Title>{member.Name}</Card.Title>
              <Card.Text>
                <strong>Email:</strong> {member.Email || 'N/A'}<br />
                <strong>Title:</strong> {member.Title || 'N/A'}<br />
                <strong>Company:</strong> {member.Company || 'N/A'}<br />
                <strong>Phone:</strong> {member['Phone Number'] || 'N/A'}<br />
                <strong>Instagram:</strong>{' '}
                {member.Instagram ? (
                  <a 
                    href={formatInstagramLink(member.Instagram)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    {member.Instagram}
                    <i className="bi bi-instagram ms-1"></i>
                  </a>
                ) : 'N/A'}
              </Card.Text>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Flight Information */}
      {member['Flight # incoming'] && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              Flight Information
              {flightStatus && getStatusBadge(flightStatus.status)}
            </h5>
          </Card.Header>
          <Card.Body>
            <Card.Text>
              <strong>Flight Number:</strong> {member['Flight # incoming']}<br />
              <strong>Scheduled Arrival:</strong> {member['Flight arrival time'] || 'N/A'}<br />
              <strong>Scheduled Departure After Trip:</strong> {member['Flight departure time'] || 'N/A'}
            </Card.Text>

            {member['Flight # incoming'] && (
              <>
                <h6 className="mt-3">
                  <a 
                    href={`https://www.google.com/search?q=${encodeURIComponent(member['Flight # incoming'] + ' status')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                  >
                    Live Status Check <i className="bi bi-box-arrow-up-right ms-1"></i>
                  </a>
                </h6>
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Additional Information */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">Additional Information</h5>
        </Card.Header>
        <Card.Body>
          <Card.Text>
            <strong>Car Rental:</strong> {member['Car Rented'] || 'No'}<br />
            <strong>Travel Insurance:</strong> {member['Travel Insurance Policy'] || 'No'}
          </Card.Text>
        </Card.Body>
      </Card>
    </div>
  );
} 